import { useState, type FormEvent } from 'react';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/db69qba2sgkaik1und30';
const MESSAGE_LIMIT = 500;

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      setMessageLength(0);
      return;
    }
    formData.delete('website_alt');

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        '';

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
        setMessageLength(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center rounded-lg border border-secondary-200 bg-secondary-50 px-6 py-12 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-500 text-2xl text-background-50">
          <i className="ri-check-line" />
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
          提交成功
        </h3>
        <p className="mt-2 max-w-sm text-sm text-foreground-600">
          感谢你的反馈，我们会尽快查看并处理。当前为前端演示站点，回复将通过邮件进行。
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-5 inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 text-sm font-label text-foreground-800 transition-colors hover:bg-background-100"
        >
          <i className="ri-refresh-line" />
          再次填写
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7"
      noValidate
    >
      <h2 className="font-heading text-lg font-bold text-foreground-950">
        意见反馈 / 联系我们
      </h2>
      <p className="mt-1.5 text-sm text-foreground-600">
        有问题、建议或内容纠错，欢迎留言告诉我们。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-label text-foreground-700">
            你的称呼 <span className="text-accent-600">*</span>
          </span>
          <input
            type="text"
            name="name"
            required
            placeholder="请输入称呼"
            className="h-11 rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-label text-foreground-700">
            电子邮箱 <span className="text-accent-600">*</span>
          </span>
          <input
            type="email"
            name="email"
            required
            placeholder="name@example.com"
            className="h-11 rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="text-sm font-label text-foreground-700">
            反馈类型
          </span>
          <select
            name="topic"
            defaultValue="内容纠错与建议"
            className="h-11 cursor-pointer rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-950 focus:border-primary-400 focus:outline-none"
          >
            <option value="内容纠错与建议">内容纠错与建议</option>
            <option value="资源收录咨询">资源收录咨询</option>
            <option value="版权相关反馈">版权相关反馈</option>
            <option value="合作与商务">合作与商务</option>
            <option value="其他问题">其他问题</option>
          </select>
        </label>

        <label className="flex flex-col gap-1.5 sm:col-span-2">
          <span className="flex items-center justify-between text-sm font-label text-foreground-700">
            <span>
              详细内容 <span className="text-accent-600">*</span>
            </span>
            <span className="text-xs text-foreground-500">
              {messageLength}/{MESSAGE_LIMIT}
            </span>
          </span>
          <textarea
            name="message"
            required
            maxLength={MESSAGE_LIMIT}
            rows={5}
            onChange={(event) => setMessageLength(event.target.value.length)}
            placeholder="请详细描述你的问题或建议（最多 500 字）"
            className="resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
          />
        </label>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-hidden-guard"
      />

      {status === 'error' && formError && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2 text-sm text-accent-900">
          <i className="ri-error-warning-line" />
          {formError}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} />
          {status === 'submitting' ? '提交中…' : '提交反馈'}
        </button>
        <span className="text-xs text-foreground-500">
          提交即表示你同意本站的使用须知与版权声明
        </span>
      </div>
    </form>
  );
}